import { GameState } from '@ddt/game-engine';
import {
  ErrorCode,
  MatchClient,
  MatchRequestError,
  msg,
  type FriendPresence,
  type ConnectionState,
  type MinimalWebSocket,
  type QueueStatus,
  type RoomClosedReason,
  type RoomInvite,
  type RoomState,
  type ServerMessage,
  type WebSocketFactory,
} from '@ddt/protocol';
import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import { getServices } from '../services';

/** Texto para cada erro do protocolo. A interface decide pelo código, nunca pelo texto do servidor. */
export const ERROR_TEXT: Partial<Record<ErrorCode, string>> = {
  [ErrorCode.INVALID_MESSAGE]: 'Pedido inválido.',
  [ErrorCode.NOT_IN_ROOM]: 'Você não está em uma sala.',
  [ErrorCode.ALREADY_IN_ROOM]: 'Você já está em uma sala. Saia dela primeiro.',
  [ErrorCode.ROOM_NOT_FOUND]: 'Essa sala não existe mais.',
  [ErrorCode.ROOM_FULL]: 'A sala está cheia.',
  [ErrorCode.WRONG_PASSWORD]: 'Senha da sala incorreta.',
  [ErrorCode.NOT_ROOM_OWNER]: 'Só o dono da sala pode fazer isso.',
  [ErrorCode.NOT_READY]: 'Todos precisam estar prontos e os dois times precisam ter jogadores.',
  [ErrorCode.GAME_IN_PROGRESS]: 'A partida já começou.',
  [ErrorCode.ALREADY_QUEUED]: 'Você já está na fila. Saia dela primeiro.',
  [ErrorCode.WEAPON_REQUIRED]: 'Todos do grupo precisam de uma arma equipada para jogar.',
  [ErrorCode.ROOM_QUEUED]: 'A sala está na fila. O dono precisa cancelar a busca.',
  [ErrorCode.NOT_FRIEND]: 'Esse jogador não está na sua lista de amigos.',
  [ErrorCode.FRIEND_UNAVAILABLE]: 'O amigo está offline, em partida ou já na sua sala.',
  [ErrorCode.INVITE_TOO_SOON]: 'Aguarde alguns segundos para repetir.',
  [ErrorCode.FRIENDS_UNAVAILABLE]: 'Os amigos não estão disponíveis agora. Tente de novo em instantes.',
  [ErrorCode.NOT_YOUR_TURN]: 'Não é a sua vez.',
  [ErrorCode.NOT_IN_GAME]: 'Você não está em uma partida.',
  [ErrorCode.NO_ACTION_POINTS]: 'Sem pontos de ação (PA) para isso.',
  [ErrorCode.BUSY]: 'Aguarde o tiro terminar.',
  [ErrorCode.RATE_LIMITED]: 'Calma: ações demais em pouco tempo.',
  [ErrorCode.UNAVAILABLE]: 'Não foi possível iniciar a partida agora. Tente de novo.',
  [ErrorCode.NO_SPECIAL]: 'A bomba especial só está disponível com a fúria cheia.',
  [ErrorCode.ALREADY_FIRED]: 'Você já atirou neste turno.',
  [ErrorCode.UNKNOWN_SKILL]: 'Você não tem essa habilidade.',
  [ErrorCode.SKILL_ON_COOLDOWN]: 'A habilidade ainda está em recarga.',
  [ErrorCode.INVALID_TARGET]: 'Alvo inválido para esta habilidade.',
};

export function errorText(code: ErrorCode, detail = ''): string {
  // Sem arma: o servidor manda os apelidos de quem não tem; a mensagem diz quem precisa equipar.
  if (code === ErrorCode.WEAPON_REQUIRED && detail) return `Sem arma equipada: ${detail}. Equipe uma arma para jogar.`;
  return ERROR_TEXT[code] ?? (detail || 'Erro inesperado.');
}

export type MatchPhase = 'offline' | 'lobby' | 'queue' | 'room' | 'playing';

export interface MatchStoreOptions {
  webSocketFactory?: WebSocketFactory;
  /** Mantém `request` com prazo curto nos testes. */
  requestTimeoutMs?: number;
}

let testOptions: MatchStoreOptions = {};

/** Só para testes: injeta o WebSocket falso antes de criar a store. */
export function setMatchStoreOptions(options: MatchStoreOptions): void {
  testOptions = options;
}

export const useMatch = defineStore('match', () => {
  const connection = ref<ConnectionState>('idle');
  const room = shallowRef<RoomState | null>(null);
  /** Por que a última sala fechou para este jogador (saiu, expulso...). Só a tela da sala lê isto, para avisar quem foi expulso. */
  const roomClosedReason = ref<RoomClosedReason | null>(null);
  const queue = shallowRef<QueueStatus | null>(null);
  const notice = ref('');
  /** Convites de sala recebidos de amigos, do mais antigo ao mais novo. O mesmo amigo só tem um convite: o novo substitui o velho. */
  const invites = ref<RoomInvite[]>([]);
  /** Convite aceito que a tela da sala de PvP ainda precisa cumprir (sair da sala atual e entrar na do amigo). */
  const pendingInvite = shallowRef<RoomInvite | null>(null);
  const game = shallowRef(new GameState());
  /** Sobe a cada mensagem que muda o jogo; as telas que dependem de `game` leem isto para reavaliar. */
  const gameVersion = ref(0);
  const myAccountId = ref('');
  /** Força do último tiro deste jogador que o servidor confirmou (`ShotResolved`); `null` antes do primeiro tiro da partida. */
  const lastPower = ref<number | null>(null);
  /** Elevação da mira (graus acima do chão) do último tiro deste jogador que o servidor confirmou; referência do mostrador e da mira. */
  const lastAim = ref<number | null>(null);
  /** Elevação do tiro enviado e ainda não confirmado; vira `lastAim` junto com a força. */
  const pendingAim = ref<number | null>(null);
  /**
   * Ângulo absoluto (0 a 360, protocolo) do último tiro confirmado deste jogador. A referência da mira usa este valor, e não `lastAim`: a elevação é medida a partir do chão,
   * então andar para outra inclinação a moveria junto com a faixa. O ângulo absoluto fica no mesmo lugar do mundo.
   */
  const lastAngle = ref<number | null>(null);
  const pendingAngle = ref<number | null>(null);
  /** Força marcada pelo jogador na régua: só referência visual, o Espaço continua carregando a força pelo tempo. */
  const pinnedPower = ref<number | null>(null);
  /** Força do tiro enviado e ainda não confirmado; vira `lastPower` no `ShotResolved` e some em erro ou troca de turno. */
  const pendingPower = ref<number | null>(null);
  /** Há um tiro enviado esperando o servidor: a interface não aceita outro disparo nem nova marcação de força. */
  const firing = computed(() => pendingPower.value !== null);
  let client: MatchClient | null = null;

  const phase = computed<MatchPhase>(() => {
    if (connection.value !== 'open') return 'offline';
    if ((gameVersion.value >= 0 && game.value.status === 'playing') || room.value?.status === 2 /* PLAYING */) return 'playing';
    if (room.value) return 'room';
    if (queue.value?.queued) return 'queue';
    return 'lobby';
  });
  const iAmOwner = computed(() => room.value?.members.find((m) => m.accountId === myAccountId.value)?.isOwner === true);

  function connect(accountId: string): void {
    myAccountId.value = accountId;
    if (client) return;
    const { api, platform } = getServices();
    client = new MatchClient({
      url: platform.env.wsUrl,
      getToken: () => api.accessTokenForSocket(),
      webSocketFactory: testOptions.webSocketFactory ?? ((url, protocols) => new WebSocket(url, protocols) as unknown as MinimalWebSocket),
    });
    client.onState((s) => {
      connection.value = s;
      if (s === 'reconnecting') notice.value = 'Reconectando...';
      else if (s === 'open') notice.value = '';
    });
    client.onFailure((error) => {
      if (client?.state !== 'open') notice.value = error.message;
    });
    client.onMessage(onMessage);
    client.connect();
  }

  function disconnect(): void {
    client?.close();
    client = null;
    room.value = null;
    queue.value = null;
    invites.value = [];
    pendingInvite.value = null;
    game.value = new GameState();
    gameVersion.value++;
    resetPowerMarks();
    connection.value = 'idle';
  }

  function resetPowerMarks(): void {
    lastPower.value = null;
    lastAim.value = null;
    lastAngle.value = null;
    pinnedPower.value = null;
    pendingPower.value = null;
    pendingAim.value = null;
    pendingAngle.value = null;
  }

  function onMessage(message: ServerMessage): void {
    const payload = message.payload;
    switch (payload.case) {
      case 'welcome':
        if (!payload.value.hasActiveSession) {
          room.value = null;
          queue.value = null;
        }
        break;
      case 'roomInvite':
        invites.value = [...invites.value.filter((i) => i.fromAccountId !== payload.value.fromAccountId), payload.value].slice(-5);
        break;
      case 'room':
        room.value = payload.value;
        queue.value = null;
        break;
      case 'roomClosed':
        room.value = null;
        roomClosedReason.value = payload.value.reason;
        break;
      case 'queue':
        queue.value = payload.value.queued ? payload.value : null;
        break;
      case 'error':
        // Erros com requestId voltam como rejeição da própria chamada; os demais (ações de jogo) viram aviso.
        if (!message.requestId) {
          notice.value = errorText(payload.value.code, payload.value.detail);
          pendingPower.value = null;
        }
        break;
      case 'gameStarted':
        resetPowerMarks();
        break;
      case 'snapshot':
        // Reconexão na mesma partida mantém as marcas; snapshot de outra partida começa do zero.
        if (payload.value.gameId !== game.value.gameId) resetPowerMarks();
        pendingPower.value = null;
        pendingAim.value = null;
        pendingAngle.value = null;
        break;
      case 'turnStarted':
        pendingPower.value = null;
        pendingAim.value = null;
        pendingAngle.value = null;
        break;
      case 'shot':
        if (payload.value.shooterId === myAccountId.value && pendingPower.value !== null) {
          lastPower.value = pendingPower.value;
          lastAim.value = pendingAim.value;
          lastAngle.value = pendingAngle.value;
          pendingPower.value = null;
          pendingAim.value = null;
          pendingAngle.value = null;
        }
        break;
      default:
        break;
    }

    const before = game.value.status;
    game.value.apply(message);
    if (payload.case === 'gameStarted' || payload.case === 'snapshot' || payload.case === 'gameFinished' || before !== game.value.status) gameVersion.value++;
  }

  /** Envia um pedido que tem resposta direta; erros do servidor viram `Error` com texto em português. */
  async function request(payload: Parameters<MatchClient['request']>[0]): Promise<ServerMessage> {
    if (!client) throw new Error('Sem conexão com o servidor.');
    try {
      return await client.request(payload, testOptions.requestTimeoutMs ?? 5000);
    } catch (error) {
      if (error instanceof MatchRequestError) throw new Error(errorText(error.code, error.detail), { cause: error });
      throw error;
    }
  }

  async function createRoom(options: { name: string; teamSize: number; password?: string }): Promise<void> {
    await request(msg.createRoom(options));
  }

  /** Quais amigos da conta estão online (o Match busca a lista no Character). */
  async function friendsPresence(): Promise<FriendPresence[]> {
    const reply = await request(msg.friendsPresence());
    return reply.payload.case === 'friendsPresence' ? reply.payload.value.friends : [];
  }

  /** Pede ao Match que releia o visual e a arma no Character (depois de equipar na mochila): a sala mostra o boneco novo. Sem resposta direta. */
  function refreshProfile(): void {
    client?.send(msg.refreshProfile());
  }

  /** Convida um amigo para a sala em que o jogador está. */
  async function inviteFriend(accountId: string): Promise<void> {
    await request(msg.inviteFriend(accountId));
  }

  function dismissInvite(fromAccountId: string): void {
    invites.value = invites.value.filter((i) => i.fromAccountId !== fromAccountId);
  }

  /** Aceita o convite: a tela da sala de PvP troca a sala do jogador pela do amigo. */
  function acceptInvite(invite: RoomInvite): void {
    dismissInvite(invite.fromAccountId);
    pendingInvite.value = invite;
  }

  async function joinByCode(code: string): Promise<void> {
    await request(msg.joinByCode(code));
  }

  async function leaveRoom(): Promise<void> {
    await request(msg.leaveRoom());
  }

  async function kickMember(accountId: string): Promise<void> {
    await request(msg.kickMember(accountId));
  }

  async function setReady(ready: boolean): Promise<void> {
    await request(msg.setReady(ready));
  }

  async function enqueueGroup(): Promise<void> {
    await request(msg.enqueueGroup());
  }

  async function dequeue(): Promise<void> {
    await request(msg.dequeue());
  }

  function move(direction: -1 | 1): boolean {
    return client?.send(msg.move(direction)) ?? false;
  }

  /** `angle` é o ângulo absoluto do tiro e `elevation`, a mira em graus acima do chão; os dois ficam guardados como "tiro anterior" quando o servidor confirmar. */
  function fire(angle: number, power: number, special: boolean, elevation: number | null = null): boolean {
    const sent = client?.send(msg.fire(angle, power, special)) ?? false;
    if (sent) {
      pendingPower.value = power;
      pendingAim.value = elevation;
      pendingAngle.value = angle;
    }
    return sent;
  }

  /** Marca (ou, com `null`, desmarca) uma força de referência na régua. */
  function setPinnedPower(power: number | null): void {
    pinnedPower.value = power === null ? null : Math.min(100, Math.max(1, Math.round(power)));
  }

  function skipTurn(): boolean {
    return client?.send(msg.skipTurn()) ?? false;
  }

  /** Usa uma habilidade de arma ou acessório. `targetId` vazio vale para si mesmo ou, contra adversário, quando só há um. */
  function useSkill(skillId: number, targetId = ''): boolean {
    return client?.send(msg.useSkill(skillId, targetId)) ?? false;
  }

  function resume(): boolean {
    return client?.send(msg.resume()) ?? false;
  }

  function dismissNotice(): void {
    notice.value = '';
  }

  /** Depois do resultado: limpa o estado da partida. */
  function resetGame(): void {
    game.value = new GameState();
    gameVersion.value++;
    resetPowerMarks();
  }

  return {
    connection,
    room,
    roomClosedReason,
    queue,
    notice,
    invites,
    pendingInvite,
    game,
    gameVersion,
    phase,
    iAmOwner,
    myAccountId,
    lastPower,
    lastAim,
    lastAngle,
    pinnedPower,
    firing,
    connect,
    disconnect,
    createRoom,
    joinByCode,
    friendsPresence,
    refreshProfile,
    inviteFriend,
    dismissInvite,
    acceptInvite,
    leaveRoom,
    kickMember,
    setReady,
    enqueueGroup,
    dequeue,
    move,
    fire,
    setPinnedPower,
    skipTurn,
    useSkill,
    resume,
    dismissNotice,
    resetGame,
  };
});
